import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { 
  ArrowLeft, CheckCircle, Clock, BookOpen, Lightbulb, 
  Target, Star, Award, ChevronDown, ChevronUp, Play
} from 'lucide-react';
import axios from 'axios';

const CoachingLesson = () => {
  const { lessonId } = useParams();
  const navigate = useNavigate();
  const [lessonData, setLessonData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});

  useEffect(() => {
    fetchLessonContent();
  }, [lessonId]);

  const fetchLessonContent = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      console.log('🎯 Fetching lesson content for:', lessonId);
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/coaching/lessons/${lessonId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 30000 // 30 second timeout
        }
      );
      console.log('✅ Lesson content loaded successfully:', response.data);
      
      setLessonData(response.data);
      
      // Auto-expand first section
      if (response.data?.lesson_content?.main_content?.length > 0) {
        setExpandedSections({ 0: true });
      }
    } catch (error) {
      console.error('Error fetching lesson content:', error);
      if (error.response?.status === 402) {
        navigate('/subscription/plans');
      } else if (error.response?.status === 404) {
        navigate('/coaching/dashboard');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteLesson = async () => {
    setCompleting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/coaching/lessons/${lessonId}/complete`,
        {
          completion_time: new Date().toISOString(),
          sections_viewed: Object.keys(expandedSections),
          user_feedback: "Lesson completed successfully"
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        setCompleted(true);
        // Show success message with points earned
        setTimeout(() => {
          navigate('/coaching/dashboard');
        }, 2000);
      }
    } catch (error) {
      console.error('Error completing lesson:', error);
      alert('Failed to complete lesson. Please try again.');
    } finally {
      setCompleting(false);
    }
  };

  const toggleSection = (index) => {
    setExpandedSections(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-2 border-rose-500 border-t-transparent mx-auto mb-4"></div>
          <p className="text-white">Loading lesson content...</p>
        </div>
      </div>
    );
  }

  if (!lessonData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <BookOpen className="w-16 h-16 text-gray-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-4">Lesson Not Found</h2>
          <Button onClick={() => navigate('/coaching/dashboard')}>
            Return to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  const { lesson_content, lesson_info } = lessonData;

  if (completed) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="bg-green-500 rounded-full p-6 w-24 h-24 mx-auto mb-6 flex items-center justify-center">
            <CheckCircle className="w-12 h-12 text-white" />
          </div>
          <h2 className="text-3xl font-bold text-white mb-4">Lesson Completed!</h2>
          <p className="text-gray-300 mb-6">You earned 15 coaching points</p>
          <div className="animate-pulse">
            <p className="text-sm text-gray-400">Returning to dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(236,72,153,0.1),transparent_70%)]"></div>
      
      <div className="relative z-10 max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <Button
            onClick={() => navigate(-1)}
            variant="outline"
            className="bg-white/10 border-white/20 text-white hover:bg-white/20"
          >
            <ArrowLeft className="mr-2" size={16} />
            Back
          </Button>
          
          <div className="flex items-center space-x-2">
            <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">
              {lesson_info.content_type}
            </Badge>
            <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">
              VIP Coaching
            </Badge>
          </div>
        </div>

        {/* Lesson Header */}
        <Card className="bg-white/5 backdrop-blur-md border-white/10 mb-8">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-2xl text-white mb-2">{lesson_info.title}</CardTitle>
                <p className="text-gray-300 mb-4">{lesson_info.description}</p>
                
                <div className="flex items-center space-x-4 text-sm text-gray-400">
                  <div className="flex items-center">
                    <Clock className="w-4 h-4 mr-1" />
                    <span>{lesson_info.estimated_time}</span>
                  </div>
                  <div className="flex items-center">
                    <Target className="w-4 h-4 mr-1" />
                    <span>15 points</span>
                  </div>
                </div>
              </div>
              
              <Play className="w-12 h-12 text-rose-500" />
            </div>
          </CardHeader>
        </Card>

        {/* Introduction */}
        <Card className="bg-gradient-to-r from-blue-500/10 to-cyan-500/10 border-blue-500/20 mb-6">
          <CardContent className="p-6">
            <h3 className="text-lg font-semibold text-white mb-3 flex items-center">
              <Lightbulb className="w-5 h-5 mr-2 text-yellow-500" />
              Introduction
            </h3>
            <p className="text-gray-300 leading-relaxed">{lesson_content.introduction}</p>
          </CardContent>
        </Card>

        {/* Main Content Sections */}
        <div className="space-y-4 mb-8">
          <h3 className="text-lg font-semibold text-white">Lesson Content</h3>
          
          {lesson_content.main_content.map((section, index) => (
            <Card key={index} className="bg-white/5 backdrop-blur-md border-white/10">
              <CardHeader
                className="cursor-pointer"
                onClick={() => toggleSection(index)}
              >
                <div className="flex items-center justify-between">
                  <CardTitle className="text-white text-lg">{section.section_title}</CardTitle>
                  {expandedSections[index] ? (
                    <ChevronUp className="w-5 h-5 text-gray-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400" />
                  )}
                </div>
              </CardHeader>
              
              {expandedSections[index] && (
                <CardContent className="pt-0">
                  <div className="space-y-6">
                    <div className="text-gray-300 leading-relaxed">
                      {section.content}
                    </div>
                    
                    {section.activity && (
                      <div className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/20 rounded-lg p-4">
                        <h4 className="font-semibold text-white mb-2 flex items-center">
                          <Target className="w-4 h-4 mr-2 text-purple-400" />
                          Activity
                        </h4>
                        <p className="text-gray-300 text-sm">{section.activity}</p>
                      </div>
                    )}
                    
                    {section.tips && section.tips.length > 0 && (
                      <div className="bg-gradient-to-r from-green-500/10 to-emerald-500/10 border border-green-500/20 rounded-lg p-4">
                        <h4 className="font-semibold text-white mb-3 flex items-center">
                          <Star className="w-4 h-4 mr-2 text-green-400" />
                          Pro Tips
                        </h4>
                        <ul className="space-y-2">
                          {section.tips.map((tip, tipIndex) => (
                            <li key={tipIndex} className="flex items-start text-gray-300 text-sm">
                              <div className="w-1.5 h-1.5 bg-green-400 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                              {tip}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </CardContent>
              )}
            </Card>
          ))}
        </div>

        {/* Practice Exercises */}
        {lesson_content.practice_exercises && lesson_content.practice_exercises.length > 0 && (
          <Card className="bg-gradient-to-r from-orange-500/10 to-red-500/10 border-orange-500/20 mb-6">
            <CardHeader>
              <CardTitle className="text-white flex items-center">
                <Award className="mr-2 text-orange-500" size={20} />
                Practice Exercises
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {lesson_content.practice_exercises.map((exercise, index) => (
                  <div key={index} className="bg-white/5 rounded-lg p-4">
                    <h4 className="font-semibold text-white mb-2">{exercise.exercise_title}</h4>
                    <p className="text-gray-300 text-sm mb-3">{exercise.instructions}</p>
                    <div className="text-xs text-gray-400">
                      <strong>Expected Outcome:</strong> {exercise.expected_outcome}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Key Takeaways */}
        <Card className="bg-gradient-to-r from-yellow-500/10 to-orange-500/10 border-yellow-500/20 mb-6">
          <CardHeader>
            <CardTitle className="text-white flex items-center">
              <Star className="mr-2 text-yellow-500" size={20} />
              Key Takeaways
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-3">
              {lesson_content.key_takeaways.map((takeaway, index) => (
                <li key={index} className="flex items-start text-gray-300">
                  <CheckCircle className="w-5 h-5 text-green-400 mr-3 mt-0.5 flex-shrink-0" />
                  {takeaway}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        {/* Reflection Questions */}
        {lesson_content.reflection_questions && lesson_content.reflection_questions.length > 0 && (
          <Card className="bg-white/5 backdrop-blur-md border-white/10 mb-6">
            <CardHeader>
              <CardTitle className="text-white flex items-center">
                <Lightbulb className="mr-2 text-purple-500" size={20} />
                Reflection Questions
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {lesson_content.reflection_questions.map((question, index) => (
                  <div key={index} className="bg-white/5 rounded-lg p-4">
                    <p className="text-gray-300">{question}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Next Steps */}
        <Card className="bg-gradient-to-r from-rose-500/10 to-pink-500/10 border-rose-500/20 mb-8">
          <CardContent className="p-6">
            <h3 className="text-lg font-semibold text-white mb-3 flex items-center">
              <Target className="w-5 h-5 mr-2 text-rose-400" />
              Next Steps
            </h3>
            <p className="text-gray-300 leading-relaxed mb-4">{lesson_content.next_steps}</p>
          </CardContent>
        </Card>

        {/* Complete Lesson Button */}
        <div className="text-center">
          <Button
            onClick={handleCompleteLesson}
            disabled={completing}
            className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 px-8 py-3 text-lg"
          >
            {completing ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent mr-2"></div>
                Completing...
              </>
            ) : (
              <>
                <CheckCircle className="mr-2" size={20} />
                Complete Lesson (+15 points)
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CoachingLesson;